import React, { useState, useEffect } from 'react';
import ReactPlayer from 'react-player';
import { useMedia } from './context/MediaContext';
import { useScoreboard } from './context/ScoreboardContext';
import { useMusic } from './context/MusicContext';
import { useLanguage } from './context/LanguageContext';

const MultimediaControl = ({ onClose, openVideoPlayer, isVideoPlaying, currentVideoIndex }) => {
  const [previewingIndex, setPreviewingIndex] = useState(null);
  const { youtubeVideos, youtubePlaylistUrl } = useMedia();
  const { isEntertainmentEnabled } = useScoreboard();
  const { isMusicPlaying, setIsMusicPlaying } = useMusic();
  const { t } = useLanguage();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleVideoSelect = (index) => {
    if (isVideoPlaying && currentVideoIndex === index) {
      if (window.videoPlayerWindow) {
        window.videoPlayerWindow.postMessage({ type: 'STOP_VIDEO' }, '*');
      }
    } else if (isVideoPlaying) {
      return;
    } else {
      openVideoPlayer(index);
    }
  };

  const handleStopAndCloseVideoPlayer = () => {
    if (window.videoPlayerWindow && !window.videoPlayerWindow.closed) {
      window.videoPlayerWindow.postMessage({ type: 'STOP_VIDEO' }, '*');
      window.videoPlayerWindow.close();
      setTimeout(() => {
        window.videoPlayerWindow = null;
      }, 500);
    }
  };

  const isVideoPlayerOpen = window.videoPlayerWindow && !window.videoPlayerWindow.closed;

  const loopVideos = youtubeVideos.filter(video => video.url && video.loop);
  const introVideos = youtubeVideos.filter(video => video.url && !video.loop);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">{t('multimedia.title')}</h1>
          <div className="flex items-center gap-3">
            {isVideoPlayerOpen && (
              <button
                onClick={handleStopAndCloseVideoPlayer}
                className="bg-amber-500 text-black px-4 py-2 rounded-lg hover:bg-amber-600 font-bold text-base"
                title={t('multimedia.stopVideo')}
              >
                ⏹️ {t('multimedia.stopAndClose')}
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="mb-6 p-6 bg-blue-50 border-2 border-blue-200 rounded-lg">
          <h3 className="text-lg font-bold text-blue-900 mb-3">📋 {t('multimedia.workflow')}</h3>
          <div className="space-y-2 text-sm text-blue-800">
            <div className="flex items-start gap-3">
              <span className="font-bold bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">1</span>
              <div>
                {t('multimedia.step1')}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="font-bold bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">2</span>
              <div>
                {t('multimedia.step2')}
              </div>
            </div>
          </div>
          <p className="text-xs text-blue-600 mt-3 italic">
            💡 {t('multimedia.tip')}
          </p>
        </div>

        {!isEntertainmentEnabled ? (
          <div className="text-center text-gray-500 py-8">
            {t('multimedia.licenseRequired')}
          </div>
        ) : (
          <div className="space-y-8">
            {/* Loop Videos/Music and Intro Videos Side by Side */}
            <div className="grid grid-cols-2 gap-6">
              {/* Loop Videos and Music Section */}
              <div className="space-y-6">
                {/* Loop Videos */}
                <div className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                    <h2 className="text-xl font-bold text-blue-900">{t('multimedia.loopVideos')}</h2>
                  </div>
                  <p className="text-sm text-blue-700 mb-4 font-medium">⏰ {t('multimedia.loopVideosDesc')}</p>
                  {loopVideos.length > 0 ? (
                    <div className="space-y-4">
                      {loopVideos.map((video, originalIndex) => {
                        const index = youtubeVideos.indexOf(video);
                        return (
                          <div
                            key={index}
                            className="flex flex-col space-y-2 p-4 rounded-lg hover:bg-gray-50 bg-white border border-gray-200"
                          >
                            <div 
                              data-testid={`video-${index}-preview`}
                            className={`w-full h-32 bg-gray-100 rounded overflow-hidden cursor-pointer relative ${previewingIndex === index ? 'ring-2 ring-blue-500' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setPreviewingIndex(previewingIndex === index ? null : index);
                              }}
                              title={previewingIndex === index ? 'Click to stop preview' : 'Click to preview'}
                            >
                              <div style={{ pointerEvents: 'none', position: 'relative', width: '100%', height: '100%' }}>
                                <ReactPlayer
                                  url={video.url}
                                  width="100%"
                                  height="100%"
                                  playing={previewingIndex === index}
                                  muted={true}
                                  controls={false}
                                  light={true}
                                  config={{
                                    youtube: {
                                      playerVars: {
                                        modestbranding: 1,
                                        controls: 0,
                                        showinfo: 0,
                                        rel: 0,
                                        disablekb: 1,
                                        playsinline: 1
                                      }
                                    }
                                  }}
                                />
                              </div>
                            </div>
                            <button
                              onClick={() => handleVideoSelect(index)}
                              data-testid={`video-${index}-${
                                isVideoPlaying && currentVideoIndex === index ? 'pause' : 'play'
                              }-button`}
                              className={`w-full py-2 text-white rounded-lg flex items-center justify-center transition-colors duration-200 ${
                                isVideoPlaying && currentVideoIndex === index 
                                  ? 'bg-blue-500 hover:bg-blue-600' 
                                  : isVideoPlaying 
                                    ? 'bg-gray-400 cursor-not-allowed' 
                                    : 'bg-amber-500 hover:bg-amber-600'
                              }`}
                              disabled={isVideoPlaying && currentVideoIndex !== index}
                            >
                              {isVideoPlaying && currentVideoIndex === index ? (
                                <>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2">
                                    <rect x="6" y="4" width="4" height="16" />
                                    <rect x="14" y="4" width="4" height="16" />
                                  </svg>
                                  Stop
                                </>
                              ) : (
                                <>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                  Play
                                </>
                              )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-4 bg-gray-50 rounded-lg">
                      {t('multimedia.noLoopVideos')}
                    </div>
                  )}
                </div>

                {/* Music Section */}
                <div className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                    <h2 className="text-xl font-bold text-blue-900">{t('multimedia.music')}</h2>
                  </div>
                  <p className="text-sm text-blue-700 mb-4 font-medium">🎵 {t('multimedia.musicDesc')}</p>
                  
                  {youtubePlaylistUrl ? (
                    <div className="flex flex-col space-y-2 p-4 rounded-lg hover:bg-gray-50 bg-white border border-gray-200">
                      <button
                        onClick={() => {
                          setIsMusicPlaying(!isMusicPlaying);
                        }}
                        className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-lg px-8 py-3 flex items-center justify-center transition-colors duration-200 text-lg w-full"
                        title={isMusicPlaying ? t('multimedia.pauseMusic') : t('multimedia.playMusic')}
                      >
                        {isMusicPlaying ? (
                          <>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="mr-2">
                              <rect x="6" y="4" width="4" height="16" />
                              <rect x="14" y="4" width="4" height="16" />
                            </svg>
                            {t('multimedia.pauseMusic')}
                          </>
                        ) : (
                          <>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="mr-2">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                            {t('multimedia.playMusic')}
                          </>
                        )}
                      </button>
                      <div className="text-xs text-center text-gray-500">
                        {t('multimedia.musicContinues')}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-4 bg-gray-50 rounded-lg">
                      {t('multimedia.noPlaylist')}
                    </div>
                  )}
                </div>
              </div>

              {/* Intro Videos Section */}
              <div className="border-2 border-green-300 rounded-lg p-4 bg-green-50">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-bold bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
                  <h2 className="text-xl font-bold text-green-900">{t('multimedia.introVideos')}</h2>
                </div>
                <p className="text-sm text-green-700 mb-4 font-medium">⏰ {t('multimedia.introVideosDesc')}</p>
                {introVideos.length > 0 ? (
                  <div className="space-y-4">
                    {introVideos.map((video, originalIndex) => {
                      const index = youtubeVideos.indexOf(video);
                      return (
                        <div
                          key={index}
                          className="flex flex-col space-y-2 p-4 rounded-lg hover:bg-gray-50 bg-white border border-gray-200"
                        >
                          <div 
                            data-testid={`video-${index}-preview`}
                            className={`w-full h-32 bg-gray-100 rounded overflow-hidden cursor-pointer relative ${previewingIndex === index ? 'ring-2 ring-blue-500' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewingIndex(previewingIndex === index ? null : index);
                            }}
                            title={previewingIndex === index ? 'Click to stop preview' : 'Click to preview'}
                          >
                            <div style={{ pointerEvents: 'none', position: 'relative', width: '100%', height: '100%' }}>
                              <ReactPlayer
                                url={video.url}
                                width="100%"
                                height="100%"
                                playing={previewingIndex === index}
                                muted={true}
                                controls={false}
                                light={true}
                                config={{
                                  youtube: {
                                    playerVars: {
                                      modestbranding: 1,
                                      controls: 0,
                                      showinfo: 0,
                                      rel: 0,
                                      disablekb: 1,
                                      playsinline: 1
                                    }
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <button
                            onClick={() => handleVideoSelect(index)}
                            data-testid={`video-${index}-${
                              isVideoPlaying && currentVideoIndex === index ? 'pause' : 'play'
                            }-button`}
                            className={`w-full py-2 text-white rounded-lg flex items-center justify-center transition-colors duration-200 ${
                              isVideoPlaying && currentVideoIndex === index 
                                ? 'bg-blue-500 hover:bg-blue-600' 
                                : isVideoPlaying 
                                  ? 'bg-gray-400 cursor-not-allowed' 
                                  : 'bg-amber-500 hover:bg-amber-600'
                            }`}
                            disabled={isVideoPlaying && currentVideoIndex !== index}
                          >
                            {isVideoPlaying && currentVideoIndex === index ? (
                              <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2">
                                  <rect x="6" y="4" width="4" height="16" />
                                  <rect x="14" y="4" width="4" height="16" />
                                </svg>
                                Stop
                              </>
                            ) : (
                              <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="mr-2">
                                  <path d="M8 5v14l11-7z"/>
                                </svg>
                                Play
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-4 bg-gray-50 rounded-lg">
                    {t('multimedia.noIntroVideos')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultimediaControl;
